import { FC, ReactElement, useEffect, useState } from 'react'
import styled from 'styled-components'
import { device } from '../../breakpoints'
import { IoIosClose } from 'react-icons/io'
import { AiFillStar } from 'react-icons/ai'
import { AiFillHeart } from 'react-icons/ai'
import { MdOutlineKeyboardArrowDown } from 'react-icons/md'
import {
  TiArrowUnsorted,
  TiArrowSortedUp,
  TiArrowSortedDown,
} from 'react-icons/ti'
import { MdCheckBoxOutlineBlank } from 'react-icons/md'
import { MdCheckBox } from 'react-icons/md'

interface FiltersProps {
  data: any
  handleFiltersChange: (data: any) => void
}

const Filters: FC<FiltersProps> = ({
  data,
  handleFiltersChange,
}): ReactElement => {
  const [filters, setFilters] = useState(new Map())

  // Gather all the services providers from the data
  const streamingServiceOptions = data
    .reduce((accumulator: any, currentValue: any) => {
      const providerName = currentValue.titleHeader.split(' sur ')[1]
      if (!accumulator.find((item: any) => item.value === providerName)) {
        return [
          {
            value: providerName,
            label: providerName,
            count: 1,
          },
          ...accumulator,
        ]
      } else {
        accumulator.find((item: any) => item.value === providerName).count += 1
        return accumulator
      }
    }, [])
    .map((item: any) => ({
      value: item.value,
      label: item.label + ' (' + item.count + ')',
    }))
    .sort((a: any, b: any) => a.label.localeCompare(b.label))

  // Gather all the types from the data
  const typeOptions = data
    .reduce((accumulator: any, currentValue: any) => {
      const type = currentValue.titleHeader.split(' sur ')[0]
      if (!accumulator.find((item: any) => item.value === type)) {
        return [
          {
            value: type,
            label: type,
            count: 1,
          },
          ...accumulator,
        ]
      } else {
        accumulator.find((item: any) => item.value === type).count += 1
        return accumulator
      }
    }, [])
    .map((item: any) => ({
      value: item.value,
      label: item.label + ' (' + item.count + ')',
    }))
    .sort((a: any, b: any) => a.label.localeCompare(b.label))

  useEffect(() => {
    handleFiltersChange(filters)
  }, [filters, handleFiltersChange])

  const handleDropdownChange = (type: string, value: string) => {
    console.log('handleDropdownChange', type, value)
    const newFilters = filters
    newFilters.has(type)
      ? newFilters.get(type).has(value)
        ? newFilters.get(type).delete(value)
        : newFilters.set(type, new Set([...newFilters.get(type), value]))
      : newFilters.set(type, new Set([value]))

    setFilters(new Map(newFilters))
  }

  const handleSortChange = (type: string, value: string) => {
    console.log('handleSortChange', type, value)
    const newFilters = filters

    if (newFilters.has(type)) {
      newFilters.delete(type)
    }

      newFilters.set(type, value)

    setFilters(new Map(newFilters))
  }

  const handleCheckboxChange = (type: string, value: boolean) => {
    console.log('handleCheckboxChange', type, value)
    const newFilters = filters

    if (value === false) {
      newFilters.delete(type)
    } else {
      newFilters.set(type, new Set([value]))
    }
    setFilters(new Map(newFilters))
  }

  return (
    <Container>
      <Content>
        <Dropdown
          placeholder='Plateforme'
          handleChange={(value: string) => {
            handleDropdownChange('provider', value)
          }}
          items={streamingServiceOptions}
        />
        <Dropdown
          placeholder='Type de contenu'
          handleChange={(value: string) => {
            handleDropdownChange('type', value)
          }}
          items={typeOptions}
        />
        <Checkbox
          label='Coup de coeur'
          onChange={(active: boolean) => {
            handleCheckboxChange('liked', active)
          }}
        />

        {/* Trier
            - Les mieux notés par le public
            - Les mieux notés par la rédaction
            - Les plus populaires
        */}

        <TwoCols>
        <Dropdown
          showValue={true}
          placeholder='Trier'
          handleChange={(value: string) => {
            handleSortChange('sort', value)
          }}
          items={[
            {
              label: 'Les mieux notés par le public',
              value: 'publicRating',
            },
            {
              label: 'Les mieux notés par la rédaction',
              value: 'note',
            },
            {
              label: 'Les plus populaires',
              value: 'publicCount',
            },
          ]}
        />
        </TwoCols>
      </Content>
      <FiltersContainer>
        {[...filters.keys()]
          .filter(
            (i: string) =>
              !['publicCount', 'publicRating', 'note', 'liked', 'sort'].includes(i)
          )
          .map((type) =>
            [...filters.get(type)].map((key) => (
              <Filter
                key={`filter-${type}-${key}`}
                type={type}
                handleClose={(type, value) => {
                  const newFilters = filters
                  newFilters.get(type).delete(value)
                  setFilters(new Map(newFilters))
                }}
                value={key}
                name={key}
              />
            ))
          )}
      </FiltersContainer>
    </Container>
  )
}

const FiltersContainer = styled.div``

const Container = styled.div`
`

const TwoCols = styled.div`
grid-column: span 3;

@media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
  grid-column: span 1;
}

@media ${device.tablet}, @media ${device.laptop} {
  grid-column: span 3;
}
`

const SelectContainer = styled.div`
  position: relative;
  font-family: var(--font-plex);
  font-size: 1.6rem;
`

const Content = styled.div`
  position: relative;
  text-align: left;
  display: grid;
  grid-gap: 1rem;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    grid-template-columns: 1fr;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    grid-template-columns: 1fr 1fr 1fr;
  }
`

export default Filters

const Dropdown = ({
  items,
  placeholder,
  handleChange,
  showValue,
}: {
  items: { label: string; value: string }[]
  placeholder: string
  handleChange: (value: string) => void
  showValue?: boolean
}) => {
  const [value, setValue] = useState('placeholder')

  return (
    <SelectContainer>
      <CustomSelect
        value={value}
        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
          handleChange(event.target.value)
          if (showValue) setValue(event.target.value)
        }}
      >
        <option value='placeholder' disabled selected>
          {placeholder}
        </option>
        {items.map((item) => (
          <option key={`option-${item.value}`} value={item.value}>
            {item.label}
          </option>
        ))}
      </CustomSelect>
      <ArrowContainer>
        <MdOutlineKeyboardArrowDown />
      </ArrowContainer>
    </SelectContainer>
  )
}
const CustomSelect = styled.select`
  // A reset of styles, including removing the default dropdown arrow
  appearance: none;
  // Additional resets for further consistency
  background-color: transparent;
  border: none;
  padding: 0 1em 0 0;
  margin: 0;
  width: 100%;
  font-family: inherit;
  font-size: inherit;
  cursor: inherit;
  line-height: inherit;
  outline: none;
  -webkit-font-smoothing: antialiased;

  font-family: var(--font-plex);
  font-size: 1.6rem;
  width: 100%;
  min-width: 15ch;
  padding: 15px 20px;
  cursor: pointer;
  line-height: 1.1;
  background-color: var(--yellow-main);
  color: ${({ theme }) => theme.colorMode === 'dark' ? '#121212' : theme.colorText};

  &:-ms-expand {
    display: none;
  }

  &:hover {
    opacity: 0.65;
  }
`

const ResetFilter = styled.div`
  position: absolute;
  top: 0;
  right: 42px;
  width: 40px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 2.2rem;

  &:after {
    content: '';
    width: 1px;
    height: 50%;
    background-color: rgba(0, 0, 0, 0.05);
    position: absolute;
    top: 0;
    transform: translateY(50%);
    right: 0;
  }
`

const ArrowContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 50px;
  font-size: 2.2rem;
  cursor: pointer;
  pointer-events: none;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colorMode === 'dark' ? '#121212' : theme.colorText};
`

const Filter = ({
  type,
  name,
  value,
  handleClose,
}: {
  type: string
  name: string
  value: string
  handleClose: (type: string, value: string) => void
}) => {
  return (
    <FilterContainer
      onClick={() => {
        handleClose(type, value)
      }}
    >
      <>{name}</>
      <CloseButton>
        <IoIosClose />
      </CloseButton>
    </FilterContainer>
  )
}

const FilterContainer = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  padding: 1rem 1rem;
  font-family: var(--font-plex);
  color: ${({ theme }) => theme.colorMode === 'dark' ? '#121212' : '#ffffff'};
  cursor: pointer;
  margin: 1rem 0.25rem 0 0;
  font-size: 1.5rem;
  background-color: ${({ theme }) => theme.colorText};

  &:hover {
    opacity: 0.65;
  }
`

const CloseButton = styled.div``

const Checkbox = ({
  label,
  onChange,
}: {
  label: string
  onChange: (active: boolean) => void
}) => {
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    onChange(checked)
  }, [checked])

  return (
    <CheckboxContainer
      onClick={() => {
        setChecked(!checked)
      }}
    >
      <CheckboxLabel>{label}</CheckboxLabel>
      {checked ? <MdCheckBox /> : <MdCheckBoxOutlineBlank />}
    </CheckboxContainer>
  )
}

const SortBox = ({
  label,
  name,
  onChange,
  onClick,
  checked,
  state,
}: {
  label: string
  name: string
  onChange: (value: string) => void
  onClick: () => void
  checked?: boolean
  state: 0 | 1 | 2
}) => {
  const [s, setS] = useState<number>(state)
  const icon = [<TiArrowUnsorted />, <TiArrowSortedDown />, <TiArrowSortedUp />]

  useEffect(() => {
    setS(state)
  }, [state])

  return (
    <CheckboxContainer
      onClick={() => {
        const newS = (s + 1) % 3
        setS(newS)
        onChange(newS.toString())
        onClick()
      }}
    >
      <CheckboxLabel>{label}</CheckboxLabel>
      {icon[s]}
    </CheckboxContainer>
  )
}

const CheckboxContainer = styled.div`
  display: inline-flex;
  font-family: var(--font-plex);
  font-size: 1.6rem;
  padding: 15px 20px;
  cursor: pointer;
  line-height: 1.1;
  background-color: var(--yellow-main);
  color: ${({ theme }) => theme.colorMode === 'dark' ? '#121212' : theme.colorText};
  justify-content: space-between;
  -webkit-font-smoothing: antialiased;

  &:hover {
    opacity: 0.65;
  }

  svg {
    float: right;
  }
`
const CheckboxLabel = styled.div``
